import { supabase } from '../supabase';
import { config } from '../config';

export interface PlatformStats {
  totalProducts: number;
  totalBatches: number;
  totalReviews: number;
  totalMembers: number;
  gradeDistribution: {
    'A+': number;
    'A': number;
    'B+': number;
    'B': number;
    'C+': number;
    'C': number;
    'D': number;
  };
}

export interface CultivatorLeaderboard {
  cultivator: string;
  avgRating: number;
  productCount: number;
  reviewCount: number;
  consistency: number; // Standard deviation - lower is better
}

export interface ClinicLeaderboard {
  id: string;
  name: string;
  avgProductRating: number;
  reviewCount: number;
  productCount: number;
  verifiedMemberCount: number;
}

/**
 * Helper function to calculate grade from average stars
 */
const getGradeFromStars = (avgStars: number): string => {
  if (avgStars >= 4.5) return 'A+';
  if (avgStars >= 4.0) return 'A';
  if (avgStars >= 3.5) return 'B+';
  if (avgStars >= 3.0) return 'B';
  if (avgStars >= 2.5) return 'C+';
  if (avgStars >= 2.0) return 'C';
  return 'D';
};

/**
 * Get platform-wide statistics
 */
export const getPlatformStats = async (): Promise<PlatformStats> => {
  // 🔧 DEVELOPMENT MODE - Return mock stats
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock platform stats');
    return {
      totalProducts: 147,
      totalBatches: 423,
      totalReviews: 1842,
      totalMembers: 5000,
      gradeDistribution: {
        'A+': 18,
        'A': 32,
        'B+': 41,
        'B': 28,
        'C+': 15,
        'C': 9,
        'D': 4,
      },
    };
  }

  try {
    // Get total active products
    const { count: productCount, error: productError } = await supabase
      .from('products')
      .select('*', { count: 'exact', head: true })
      .eq('active', true);

    if (productError) {
      console.error('Error counting products:', productError);
      throw productError;
    }

    // Get total distinct batch numbers
    const { data: batches, error: batchError } = await supabase
      .from('batches')
      .select('batch_no');

    if (batchError) {
      console.error('Error fetching batches:', batchError);
      throw batchError;
    }

    // Count unique batch numbers
    const uniqueBatches = new Set(batches?.map(b => b.batch_no) || []);
    const batchCount = uniqueBatches.size;

    // Get total approved reviews
    const { count: reviewCount, error: reviewError } = await supabase
      .from('reviews')
      .select('*', { count: 'exact', head: true })
      .eq('mod_state', 'approved');

    if (reviewError) {
      console.error('Error counting reviews:', reviewError);
      throw reviewError;
    }

    // Get total members using secure database function
    const { data: memberCount, error: memberError } = await supabase
      .rpc('get_member_count');

    if (memberError) {
      console.error('Error counting members:', memberError);
      // Don't throw - gracefully degrade to 0
    }

    // Calculate grade distribution from reviews
    // Get all approved reviews grouped by product with average stars
    const { data: productReviews, error: statsError } = await supabase
      .from('reviews')
      .select('product_id, stars')
      .eq('mod_state', 'approved');

    if (statsError) {
      console.error('Error fetching review stats:', statsError);
      throw statsError;
    }

    // Group by product and calculate average
    const productStarsMap = new Map<string, number[]>();
    productReviews?.forEach((review) => {
      if (!productStarsMap.has(review.product_id)) {
        productStarsMap.set(review.product_id, []);
      }
      productStarsMap.get(review.product_id)!.push(review.stars);
    });

    // Calculate grade distribution
    const gradeDistribution = {
      'A+': 0,
      'A': 0,
      'B+': 0,
      'B': 0,
      'C+': 0,
      'C': 0,
      'D': 0,
    };

    productStarsMap.forEach((stars) => {
      const avgStars = stars.reduce((a, b) => a + b, 0) / stars.length;
      const grade = getGradeFromStars(avgStars);
      gradeDistribution[grade as keyof typeof gradeDistribution]++;
    });

    return {
      totalProducts: productCount || 0,
      totalBatches: batchCount,
      totalReviews: reviewCount || 0,
      totalMembers: memberCount || 0,
      gradeDistribution,
    };
  } catch (error) {
    console.error('Error in getPlatformStats:', error);
    throw error;
  }
};

/**
 * Get cultivator leaderboard (top performers by various metrics)
 * Uses product.brand as the cultivator/producer name
 */
export const getCultivatorLeaderboard = async (): Promise<{
  byRating: CultivatorLeaderboard[];
  byConsistency: CultivatorLeaderboard[];
  byProductCount: CultivatorLeaderboard[];
}> => {
  // 🔧 DEVELOPMENT MODE - Return mock data
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock cultivator leaderboard');
    const mockCultivators: CultivatorLeaderboard[] = [
      {
        cultivator: 'Premium Cannabis Co',
        avgRating: 4.7,
        productCount: 23,
        reviewCount: 342,
        consistency: 0.3,
      },
      {
        cultivator: 'Green Leaf Co',
        avgRating: 4.5,
        productCount: 18,
        reviewCount: 287,
        consistency: 0.4,
      },
      {
        cultivator: 'Medical Cannabis Ltd',
        avgRating: 4.6,
        productCount: 31,
        reviewCount: 421,
        consistency: 0.35,
      },
      {
        cultivator: 'UK Cannabis Group',
        avgRating: 4.3,
        productCount: 15,
        reviewCount: 198,
        consistency: 0.5,
      },
      {
        cultivator: 'Northern Greens',
        avgRating: 4.4,
        productCount: 27,
        reviewCount: 356,
        consistency: 0.38,
      },
    ];

    return {
      byRating: [...mockCultivators].sort((a, b) => b.avgRating - a.avgRating).slice(0, 3),
      byConsistency: [...mockCultivators].sort((a, b) => a.consistency - b.consistency).slice(0, 3),
      byProductCount: [...mockCultivators].sort((a, b) => b.productCount - a.productCount).slice(0, 3),
    };
  }

  try {
    // Get all active products with their brands
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id, brand')
      .eq('active', true)
      .not('brand', 'is', null);

    if (productsError) {
      console.error('Error fetching products:', productsError);
      throw productsError;
    }

    // Get all approved reviews with product_id and stars
    const { data: reviews, error: reviewsError } = await supabase
      .from('reviews')
      .select('product_id, stars')
      .eq('mod_state', 'approved');

    if (reviewsError) {
      console.error('Error fetching reviews:', reviewsError);
      throw reviewsError;
    }

    // Build map of product_id to brand
    const productBrandMap = new Map<string, string>();
    products?.forEach((product) => {
      if (product.brand) {
        productBrandMap.set(product.id, product.brand);
      }
    });

    // Group reviews by cultivator (brand)
    const cultivatorMap = new Map<string, {
      ratings: number[];
      productIds: Set<string>;
    }>();

    reviews?.forEach((review) => {
      const brand = productBrandMap.get(review.product_id);
      if (!brand) return;

      if (!cultivatorMap.has(brand)) {
        cultivatorMap.set(brand, {
          ratings: [],
          productIds: new Set(),
        });
      }

      const cultivator = cultivatorMap.get(brand)!;
      cultivator.ratings.push(review.stars);
      cultivator.productIds.add(review.product_id);
    });

    // Calculate metrics for each cultivator
    const cultivators: CultivatorLeaderboard[] = Array.from(cultivatorMap.entries())
      .map(([name, data]) => {
        const avgRating = data.ratings.reduce((a, b) => a + b, 0) / data.ratings.length;
        
        // Calculate standard deviation for consistency
        const variance = data.ratings.reduce((sum, rating) => {
          return sum + Math.pow(rating - avgRating, 2);
        }, 0) / data.ratings.length;
        const consistency = Math.sqrt(variance);

        return {
          cultivator: name,
          avgRating: Number(avgRating.toFixed(2)),
          productCount: data.productIds.size,
          reviewCount: data.ratings.length,
          consistency: Number(consistency.toFixed(2)),
        };
      })
      .filter(c => c.productCount >= 2); // Only include cultivators with 2+ products

    return {
      byRating: [...cultivators].sort((a, b) => b.avgRating - a.avgRating).slice(0, 3),
      byConsistency: [...cultivators].sort((a, b) => a.consistency - b.consistency).slice(0, 3),
      byProductCount: [...cultivators].sort((a, b) => b.productCount - a.productCount).slice(0, 3),
    };
  } catch (error) {
    console.error('Error in getCultivatorLeaderboard:', error);
    throw error;
  }
};

/**
 * Get clinic leaderboard (top performers by various metrics)
 * Uses clinics, org_products, products, reviews, and profiles tables
 */
export const getClinicLeaderboard = async (): Promise<{
  byProductRating: ClinicLeaderboard[];
  byReviews: ClinicLeaderboard[];
  byProducts: ClinicLeaderboard[];
  byVerifiedMembers: ClinicLeaderboard[];
}> => {
  // 🔧 DEVELOPMENT MODE - Return mock data
  if (config.devMode.enabled && config.devMode.mockData) {
    console.log('🔧 DEV MODE: Returning mock clinic leaderboard');
    const mockClinics: ClinicLeaderboard[] = [
      {
        id: 'clinic-1',
        name: 'London Medical Cannabis Clinic',
        avgProductRating: 4.6,
        reviewCount: 432,
        productCount: 87,
        verifiedMemberCount: 1247,
      },
      {
        id: 'clinic-2',
        name: 'Manchester Cannabis Care',
        avgProductRating: 4.5,
        reviewCount: 378,
        productCount: 72,
        verifiedMemberCount: 982,
      },
      {
        id: 'clinic-3',
        name: 'Edinburgh Medical Practice',
        avgProductRating: 4.7,
        reviewCount: 521,
        productCount: 94,
        verifiedMemberCount: 1456,
      },
      {
        id: 'clinic-4',
        name: 'Birmingham Wellness Centre',
        avgProductRating: 4.4,
        reviewCount: 298,
        productCount: 63,
        verifiedMemberCount: 845,
      },
      {
        id: 'clinic-5',
        name: 'Bristol Cannabis Clinic',
        avgProductRating: 4.5,
        reviewCount: 367,
        productCount: 79,
        verifiedMemberCount: 1123,
      },
    ];

    return {
      byProductRating: [...mockClinics].sort((a, b) => b.avgProductRating - a.avgProductRating).slice(0, 3),
      byReviews: [...mockClinics].sort((a, b) => b.reviewCount - a.reviewCount).slice(0, 3),
      byProducts: [...mockClinics].sort((a, b) => b.productCount - a.productCount).slice(0, 3),
      byVerifiedMembers: [...mockClinics].sort((a, b) => b.verifiedMemberCount - a.verifiedMemberCount).slice(0, 3),
    };
  }

  try {
    // Get all active clinics
    const { data: clinics, error: clinicsError } = await supabase
      .from('clinics')
      .select('id, name')
      .eq('is_active', true);

    if (clinicsError) {
      console.error('Error fetching clinics:', clinicsError);
      throw clinicsError;
    }

    if (!clinics || clinics.length === 0) {
      console.log('No active clinics found');
      return {
        byProductRating: [],
        byReviews: [],
        byProducts: [],
        byVerifiedMembers: [],
      };
    }

    // Get org_products (clinic product associations)
    const { data: orgProducts, error: orgError } = await supabase
      .from('org_products')
      .select('org_id, product_id');

    if (orgError) {
      console.error('Error fetching org_products:', orgError);
      throw orgError;
    }

    // Get all approved reviews
    const { data: reviews, error: reviewsError } = await supabase
      .from('reviews')
      .select('product_id, stars')
      .eq('mod_state', 'approved');

    if (reviewsError) {
      console.error('Error fetching reviews:', reviewsError);
      throw reviewsError;
    }

    // Build product to reviews map
    const productReviewsMap = new Map<string, number[]>();
    reviews?.forEach((review) => {
      if (!productReviewsMap.has(review.product_id)) {
        productReviewsMap.set(review.product_id, []);
      }
      productReviewsMap.get(review.product_id)!.push(review.stars);
    });

    // Calculate metrics for each clinic
    const clinicStats = await Promise.all(
      clinics.map(async (clinic) => {
        // Get products for this clinic
        const clinicProducts = orgProducts?.filter(op => op.org_id === clinic.id) || [];
        const productIds = clinicProducts.map(cp => cp.product_id);

        // Calculate average rating and review count for clinic's products
        let totalStars = 0;
        let totalReviews = 0;
        let ratingCount = 0;

        productIds.forEach((productId) => {
          const productStars = productReviewsMap.get(productId);
          if (productStars && productStars.length > 0) {
            const productAvg = productStars.reduce((a, b) => a + b, 0) / productStars.length;
            totalStars += productAvg;
            ratingCount++;
            totalReviews += productStars.length;
          }
        });

        const avgProductRating = ratingCount > 0 ? totalStars / ratingCount : 0;

        // Get verified member count
        const { count: verifiedCount, error: verifiedError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('clinic_id', clinic.id)
          .eq('is_verified_patient', true);

        if (verifiedError) {
          console.error('Error fetching verified count for clinic:', clinic.id, verifiedError);
        }

        return {
          id: clinic.id,
          name: clinic.name,
          avgProductRating: Number(avgProductRating.toFixed(2)),
          reviewCount: totalReviews,
          productCount: productIds.length,
          verifiedMemberCount: verifiedCount || 0,
        };
      })
    );

    // Filter out clinics with no data
    const validClinics = clinicStats.filter(c => c.productCount > 0 || c.verifiedMemberCount > 0);

    return {
      byProductRating: [...validClinics]
        .filter(c => c.avgProductRating > 0)
        .sort((a, b) => b.avgProductRating - a.avgProductRating)
        .slice(0, 3),
      byReviews: [...validClinics]
        .filter(c => c.reviewCount > 0)
        .sort((a, b) => b.reviewCount - a.reviewCount)
        .slice(0, 3),
      byProducts: [...validClinics]
        .filter(c => c.productCount > 0)
        .sort((a, b) => b.productCount - a.productCount)
        .slice(0, 3),
      byVerifiedMembers: [...validClinics]
        .filter(c => c.verifiedMemberCount > 0)
        .sort((a, b) => b.verifiedMemberCount - a.verifiedMemberCount)
        .slice(0, 3),
    };
  } catch (error) {
    console.error('Error in getClinicLeaderboard:', error);
    throw error;
  }
};